import React, { useEffect, useMemo, useRef, useState } from "react";
import { Download } from "lucide-react";
import api, {
  buildUsageExportPayload,
  downloadUsageExport,
  exportUsage,
  formatApiError,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";
import WarehouseTabs from "../components/WarehouseTabs";
import { Button } from "../components/ui/button";
import { RefreshButton } from "../components/ui/refresh-button";
import {
  DataValue,
  PassiveBadge,
  PrimarySecondaryValue,
  displayValue,
  formatPresentationTimestamp,
} from "../components/ui/data-presentation";
import {
  ListPagination,
  ListTable,
  ListTableShell,
  ListTableRefreshingRow,
  ListTableState,
  ListTableViewport,
} from "../components/ui/list-table";
import {
  ColumnDateFilter,
  ColumnFilterCell,
  ColumnFilterRow,
  ColumnNumericFilter,
  ColumnSelectFilter,
  ColumnTextFilter,
  DateRangeSelector,
  matchesDateRange,
} from "../components/ui/search-filter";
import { OrdinaryPageBody, OrdinaryPageHeader } from "../components/ui/page-structure";

const EMPTY_DATE_RANGE = Object.freeze({ from: "", to: "" });
const EMPTY_APPLIED_FILTERS = Object.freeze({
  date: "", ticketNumber: "", model: "", part: "", quantity: "",
  serialNumber: "", customer: "", notes: "", createdBy: "",
});

const USAGE_TABLE_GEOMETRY = Object.freeze([
  { key: "date", kind: "fixed", width: 150 },
  { key: "ticket-number", kind: "fixed", width: 190 },
  { key: "status", kind: "fixed", width: 120 },
  { key: "source", kind: "fixed", width: 120 },
  { key: "model", kind: "fixed", width: 170 },
  { key: "part", kind: "fixed", width: 280 },
  { key: "quantity", kind: "fixed", width: 80 },
  { key: "serial-number", kind: "fixed", width: 140 },
  { key: "customer", kind: "fixed", width: 220 },
  { key: "notes", kind: "fill", minWidth: 240 },
  { key: "by", kind: "fixed", width: 200 },
]);

function includesText(value, query) {
  return String(value || "").toLowerCase().includes(String(query || "").trim().toLowerCase());
}

function matchesExactNumber(value, query) {
  return query === "" || Number(value) === Number(query);
}

export default function Usage() {
  const { warehouses } = useAuth();
  const [usage, setUsage] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [items, setItems] = useState([]);
  const [activeWh, setActiveWh] = useState(null);

  // Filters
  const [fModel, setFModel] = useState("");
  const [fItem, setFItem] = useState("");
  const [fSn, setFSn] = useState("");
  const [fCustomer, setFCustomer] = useState("");
  const [fDate, setFDate] = useState("");
  const [fQuantity, setFQuantity] = useState("");
  const [fNotes, setFNotes] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fSource, setFSource] = useState("");
  const [ticketNumberInput, setTicketNumberInput] = useState("");
  const [createdByInput, setCreatedByInput] = useState("");
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_APPLIED_FILTERS);
  const [dateRangeDraft, setDateRangeDraft] = useState(EMPTY_DATE_RANGE);
  const [dateRange, setDateRange] = useState(EMPTY_DATE_RANGE);
  const ticketNumber = appliedFilters.ticketNumber;
  const createdByEmail = appliedFilters.createdBy;
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const exportInFlightRef = useRef(false);

  const [notesPopover, setNotesPopover] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    if (!notesPopover) return;
    const close = () => setNotesPopover(null);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [notesPopover]);

  useEffect(() => {
    if (!activeWh && warehouses.length > 0) setActiveWh(warehouses[0].id);
  }, [warehouses, activeWh]);

  useEffect(() => {
    api.get("/items").then((response) => setItems(response.data));
  }, []);

  useEffect(() => {
    const params = {};
    if (fStatus) params.status = fStatus;
    if (fSource) params.source_type = fSource;
    if (ticketNumber) params.ticket_number = ticketNumber;
    if (createdByEmail) params.created_by_email = createdByEmail;
    const request = Object.keys(params).length > 0
      ? api.get("/usage", { params })
      : api.get("/usage");
    setListLoading(true);
    setListError("");
    request
      .then((response) => setUsage(response.data))
      .catch((requestError) => setListError(formatApiError(requestError)))
      .finally(() => setListLoading(false));
  }, [createdByEmail, fSource, fStatus, retryKey, ticketNumber]);

  // Map item_id -> model for quick lookup
  const itemModel = useMemo(() => {
    const m = {};
    items.forEach((it) => { m[it.id] = it.model || ""; });
    return m;
  }, [items]);

  const effectiveFilters = useMemo(
    () =>
      buildUsageExportPayload({
        warehouse_id: activeWh,
        status: fStatus,
        source_type: fSource,
        ticket_number: ticketNumber,
        created_by_email: createdByEmail,
        model: appliedFilters.model,
        item: appliedFilters.part,
        serial_number: appliedFilters.serialNumber,
        customer: appliedFilters.customer,
        date_from: dateRange.from,
        date_to: dateRange.to,
      }),
    [
      activeWh,
      fStatus,
      fSource,
      ticketNumber,
      createdByEmail,
      appliedFilters.model,
      appliedFilters.part,
      appliedFilters.serialNumber,
      appliedFilters.customer,
      dateRange.from,
      dateRange.to,
    ]
  );

  const tabCounts = useMemo(() => {
    const c = {};
    warehouses.forEach((w) => {
      c[w.id] = usage.filter((u) => u.warehouse_id === w.id).length;
    });
    return c;
  }, [usage, warehouses]);

  const filtered = useMemo(() => {
    if (!activeWh) return [];
    return usage.filter((u) => {
      if (u.warehouse_id !== activeWh) return false;
      const usageDate = String(u.created_at || "").slice(0, 10);
      if (!matchesDateRange(usageDate, dateRange)) return false;
      if (appliedFilters.date && usageDate !== appliedFilters.date) return false;
      if (fStatus && u.usage_status !== fStatus) return false;
      if (fSource && u.source_type !== fSource) return false;
      if (ticketNumber && !includesText(u.service_ticket_number, ticketNumber)) return false;
      if (createdByEmail && !includesText(u.created_by_email, createdByEmail)) return false;
      if (effectiveFilters.model) {
        const model = itemModel[u.item_id] || "";
        if (!model.toLowerCase().includes(effectiveFilters.model.toLowerCase())) return false;
      }
      if (effectiveFilters.item) {
        const q = effectiveFilters.item.toLowerCase();
        if (
          !u.part_number.toLowerCase().includes(q) &&
          !(u.part_name || "").toLowerCase().includes(q)
        )
          return false;
      }
      if (effectiveFilters.serial_number && !(u.serial_number || "").toLowerCase().includes(effectiveFilters.serial_number.toLowerCase())) return false;
      if (effectiveFilters.customer && !(u.customer_name || "").toLowerCase().includes(effectiveFilters.customer.toLowerCase())) return false;
      if (!matchesExactNumber(u.quantity, appliedFilters.quantity)) return false;
      if (appliedFilters.notes && !includesText(u.notes, appliedFilters.notes)) return false;
      return true;
    });
  }, [activeWh, appliedFilters, createdByEmail, dateRange, effectiveFilters, fSource, fStatus, itemModel, ticketNumber, usage]);

  useEffect(() => { setPage(1); }, [activeWh]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

  const activeWhObj = warehouses.find((w) => w.id === activeWh);
  const applyFilter = (name, value) => {
    setAppliedFilters((current) => ({ ...current, [name]: value }));
    setPage(1);
  };
  const applyDateRange = (value) => {
    setDateRange(value);
    setPage(1);
  };
  const handleExport = async () => {
    if (exportInFlightRef.current || !activeWh) return;
    exportInFlightRef.current = true;
    setExporting(true);
    setExportError("");
    try {
      const exportFile = await exportUsage(effectiveFilters);
      downloadUsageExport(exportFile);
    } catch (error) {
      setExportError(formatApiError(error));
    } finally {
      exportInFlightRef.current = false;
      setExporting(false);
    }
  };

  const statusPresentation = {
    active: {
      label: "Active",
      tone: "success",
    },
    legacy_active: {
      label: "Legacy Active",
      tone: "warning",
    },
    reversed: {
      label: "Reversed",
      tone: "neutral",
    },
  };

  return (
    <OrdinaryPageBody data-testid="usage-page">
      <OrdinaryPageHeader
        title="Usage"
        actions={<>
          <RefreshButton
            onClick={() => setRetryKey((current) => current + 1)}
            loading={listLoading}
            accessibleLabel="Refresh Usage"
            data-action-category="retry-refresh"
            data-testid="usage-refresh"
          />
          <Button
            type="button"
            onClick={handleExport}
            disabled={exporting || !activeWh}
            loading={exporting}
            loadingLabel="Exporting..."
            variant="export"
            data-action-category="export-download"
            data-testid="usage-export"
          >
            <Download aria-hidden="true" />
            Export
          </Button>
        </>}
        data-testid="usage-page-header"
      />
      <ListTableShell>
        <div className="flex min-w-0 flex-wrap items-end gap-3 border-b border-zinc-200" data-testid="usage-branch-date-row">
          <div className="min-w-0 flex-1">
            <WarehouseTabs
              warehouses={warehouses}
              active={activeWh}
              onChange={setActiveWh}
              counts={tabCounts}
              testidPrefix="usage-tab"
              className="border-b-0"
            />
          </div>
          <DateRangeSelector
            value={dateRangeDraft}
            onChange={setDateRangeDraft}
            onApply={applyDateRange}
            mode="immediate"
            id="usage-date-range"
            label={null}
            controlsClassName="h-9"
            className="mb-2 mr-5 w-[170px] shrink-0"
          />
        </div>

        <div className="flex items-center justify-between gap-4 border-b border-zinc-200 px-6 py-4">
          <div>
            <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-semibold">Ledger</div>
            <div className="text-base font-semibold tracking-tight">
              Spare Part Usage — {activeWhObj?.name || ""}
            </div>
          </div>
        </div>
        {exportError ? (
          <div
            className="border-b border-destructive/30 bg-destructive/5 px-6 py-3 text-sm text-destructive"
            role="alert"
            data-testid="usage-export-error"
          >
            {exportError}
          </div>
        ) : null}

        <ListTableViewport>
        <ListTable geometry={USAGE_TABLE_GEOMETRY} data-testid="usage-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Ticket Number</th>
              <th>Status</th>
              <th>Source</th>
              <th>Model</th>
              <th>Part</th>
              <th className="text-right">Qty</th>
              <th>SN</th>
              <th>Customer</th>
              <th>Notes</th>
              <th>By</th>
              
            </tr>
            <ColumnFilterRow className="bg-white">
              <ColumnFilterCell><ColumnDateFilter value={fDate} onValueChange={setFDate} onApply={(value) => applyFilter("date", value)} aria-label="Filter usage date" data-testid="usage-filter-date" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnTextFilter value={ticketNumberInput} onValueChange={setTicketNumberInput} onApply={(value) => applyFilter("ticketNumber", String(value || "").trim())} placeholder="Filter…" aria-label="Filter usage ticket number" data-testid="usage-filter-ticket-number" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnSelectFilter value={fStatus} onValueChange={setFStatus} onApply={() => setPage(1)} placeholder="All" options={[{ value: "active", label: "Active" }, { value: "legacy_active", label: "Legacy Active" }, { value: "reversed", label: "Reversed" }]} aria-label="Filter usage status" data-testid="usage-filter-status" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnSelectFilter value={fSource} onValueChange={setFSource} onApply={() => setPage(1)} placeholder="All" options={[{ value: "inventory", label: "Inventory" }, { value: "cannibal", label: "Cannibal" }]} aria-label="Filter usage source" data-testid="usage-filter-source" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnTextFilter value={fModel} onValueChange={setFModel} onApply={(value) => applyFilter("model", value)} placeholder="Filter…" aria-label="Filter usage model" data-testid="usage-filter-model" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnTextFilter value={fItem} onValueChange={setFItem} onApply={(value) => applyFilter("part", value)} placeholder="Filter…" aria-label="Filter usage part" data-testid="usage-filter-part" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnNumericFilter value={fQuantity} onValueChange={setFQuantity} onApply={(value) => applyFilter("quantity", value)} allowDecimal={false} allowNegative={false} placeholder="Qty" aria-label="Filter usage quantity" data-testid="usage-filter-quantity" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnTextFilter value={fSn} onValueChange={setFSn} onApply={(value) => applyFilter("serialNumber", value)} placeholder="Filter…" aria-label="Filter usage serial number" data-testid="usage-filter-serial-number" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnTextFilter value={fCustomer} onValueChange={setFCustomer} onApply={(value) => applyFilter("customer", value)} placeholder="Filter…" aria-label="Filter usage customer" data-testid="usage-filter-customer" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnTextFilter value={fNotes} onValueChange={setFNotes} onApply={(value) => applyFilter("notes", value)} placeholder="Filter…" aria-label="Filter usage notes" data-testid="usage-filter-notes" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnTextFilter value={createdByInput} onValueChange={setCreatedByInput} onApply={(value) => applyFilter("createdBy", String(value || "").trim())} placeholder="Filter…" aria-label="Filter usage creator" data-testid="usage-filter-created-by" /></ColumnFilterCell>
            </ColumnFilterRow>
          </thead>
          <tbody>
            <ListTableRefreshingRow active={listLoading && usage.length > 0} colSpan={11} />
            {listLoading && usage.length === 0 ? (
              <ListTableState colSpan={11} kind="loading">Loading usage records...</ListTableState>
            ) : listError ? (
              <ListTableState colSpan={11} kind="error" onRetry={() => setRetryKey((current) => current + 1)}>{listError}</ListTableState>
            ) : filtered.length === 0 ? (
              <ListTableState colSpan={11}>No usage records match.</ListTableState>
            ) : pageItems.map((u) => {
              const notes = u.notes || "";
              const fullItem = items.find((i) => i.id === u.item_id);
              const status = statusPresentation[u.usage_status];
              return (
                <tr key={u.id} data-testid={`usage-row-${u.id}`} data-usage-status={u.usage_status}>
                  <td className="whitespace-nowrap text-xs font-medium tabular-nums text-zinc-700">{formatPresentationTimestamp(u.created_at)}</td>
                  <td className="max-w-[190px] text-xs" data-testid={`usage-ticket-number-${u.id}`}><DataValue value={u.service_ticket_number} containment="truncate" /></td>
                  <td>
                    {status && (
                      <PassiveBadge
                        tone={status.tone}
                        data-testid={`usage-status-${u.id}`}
                      >
                        {status.label}
                      </PassiveBadge>
                    )}
                  </td>
                  <td className="text-xs" data-testid={`usage-source-${u.id}`}>
                    <PassiveBadge tone={u.source_type === "cannibal" ? "warning" : "info"} data-testid={`usage-source-badge-${u.id}`}>
                      {u.source_type === "cannibal" ? "Cannibal" : "Inventory"}
                    </PassiveBadge>
                    {u.source_type === "cannibal" ? (
                      <div className="mt-1 break-words text-[10px] text-muted-foreground">
                        Donor {displayValue(u.cannibal_device_serial_number)}<br />
                        Recipient {displayValue(u.service_ticket_number)} / {displayValue(u.recipient_serial_number)}
                      </div>
                    ) : null}
                  </td>
                  <td className="text-xs font-medium">
                    <DataValue value={fullItem?.model} />
                    {(fullItem?.compatible_models || []).filter((m) => m !== fullItem?.model).length > 0 && (
                      <div className="text-[10px] text-zinc-400">
                        {fullItem.compatible_models.filter((m) => m !== fullItem.model).join(", ")}
                      </div>
                    )}
                  </td>
                  <td>
                    <PrimarySecondaryValue
                      primary={u.part_name}
                      secondary={u.part_number}
                      primaryClassName="text-xs"
                      secondaryClassName="font-semibold tracking-[0.02em]"
                    />
                  </td>
                  <td className="num">{u.quantity}</td>
                  <td className="max-w-[110px] text-xs font-medium tracking-[0.02em]"><DataValue value={u.serial_number} containment="truncate" /></td>
                  <td className="max-w-[170px] text-xs"><DataValue value={u.customer_name} containment="truncate" /></td>
                  <td className="text-xs text-zinc-500" style={{whiteSpace:"nowrap"}}>
                    {!notes ? "—" : (() => {
                      const isLong = notes.length > 25;
                      return (
                        <div className="relative" style={{display:"inline-block"}}>
                          {isLong ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setNotesPopover(notesPopover === u.id ? null : u.id); }}
                              className="text-left text-zinc-500 hover:text-zinc-700 underline decoration-dotted underline-offset-2"
                              data-action-category="utility"
                            >
                              {notes.slice(0, 25)}…
                            </button>
                          ) : notes}
                          {notesPopover === u.id && (
                            <div
                            className="absolute z-20 left-0 bottom-full mb-1 bg-white border border-zinc-200 rounded-sm shadow-brutal p-3 text-xs text-zinc-700 whitespace-pre-wrap"
                              style={{minWidth:"200px", maxWidth:"280px"}}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              {notes}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="max-w-[150px] text-xs"><DataValue value={u.created_by_email} containment="truncate" /></td>
                </tr>
              );
            })}
          </tbody>
        </ListTable>
        </ListTableViewport>

        <ListPagination
          page={page}
          pageSize={pageSize}
          total={filtered.length}
          totalPages={totalPages}
          loading={listLoading}
          onPrevious={() => setPage((current) => Math.max(1, current - 1))}
          onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
          onPageSizeChange={(nextPageSize) => { setPageSize(nextPageSize); setPage(1); }}
          pageSizeAriaLabel="Usage rows per page"
          summaryTestId="usage-pagination-summary"
          pageTestId="usage-pagination-state"
          previousTestId="usage-pagination-prev"
          nextTestId="usage-pagination-next"
        />
      </ListTableShell>

    </OrdinaryPageBody>
  );
}
